import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Target, Trophy, Zap, TrendingUp, User, Award, ArrowUp, ArrowDown } from 'lucide-react';
import { collection, onSnapshot, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

const PlayerStatsPage = () => {
  const { leagueId } = useParams();
  const [league, setLeague] = useState(null);
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState({});
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('aces'); // aces, kickSmashes, akhlaaqRating, matchesPlayed
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  useEffect(() => {
    const fetchLeague = async () => {
      try {
        const leagueDoc = await getDoc(doc(db, 'leagues', leagueId));
        if (leagueDoc.exists()) {
          setLeague({ id: leagueDoc.id, ...leagueDoc.data() });
        }
      } catch (err) {
        console.error('Error fetching league:', err);
      }
    };

    fetchLeague();
  }, [leagueId]);

  useEffect(() => {
    let unsubscribePlayers = null;
    let unsubscribeTeams = null;

    const setupListeners = () => {
      // Fetch all players
      unsubscribePlayers = onSnapshot(collection(db, 'players'), (snapshot) => {
        const playersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Filter players that have played matches
        const activePlayers = playersData.filter(p => p.matchesPlayed > 0);
        setPlayers(activePlayers);
        setLoading(false);
      });

      // Fetch teams for team names
      unsubscribeTeams = onSnapshot(collection(db, 'teams'), (snapshot) => {
        const teamsData = {};
        snapshot.docs.forEach(doc => {
          teamsData[doc.id] = doc.data();
        });
        setTeams(teamsData);
      });
    };

    setupListeners();

    return () => {
      if (unsubscribePlayers) unsubscribePlayers();
      if (unsubscribeTeams) unsubscribeTeams();
    };
  }, []);

  const toggleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const sortedPlayers = [...players].sort((a, b) => {
    let aVal, bVal;
    
    switch (sortBy) {
      case 'name':
        aVal = a.name || '';
        bVal = b.name || '';
        return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      case 'aces':
        aVal = a.aces || 0;
        bVal = b.aces || 0;
        break;
      case 'kickSmashes':
        aVal = a.kickSmashes || 0;
        bVal = b.kickSmashes || 0;
        break;
      case 'unforcedErrors':
        aVal = a.unforcedErrors || 0;
        bVal = b.unforcedErrors || 0;
        break;
      case 'akhlaaqRating':
        aVal = a.akhlaaqRating || 0;
        bVal = b.akhlaaqRating || 0;
        break;
      case 'matchesPlayed':
        aVal = a.matchesPlayed || 0;
        bVal = b.matchesPlayed || 0;
        break;
      default:
        return 0;
    }
    
    return sortOrder === 'desc' ? bVal - aVal : aVal - bVal;
  });

  // Pagination logic
  const totalPages = Math.ceil(sortedPlayers.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPlayers = sortedPlayers.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #F0FDF4 0%, #FFFFFF 50%, #DBEAFE 100%)' }}>
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ marginBottom: '2rem' }}
        >
          <Link
            to={`/league/${leagueId}/leaderboard`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#6B7280',
              marginBottom: '1rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              textDecoration: 'none'
            }}
          >
            <ArrowLeft size={20} />
            Back to Leaderboard
          </Link>
          <h1 style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#1C563D' }}>
            Player Statistics - {league?.name}
          </h1>
          <p style={{ color: '#6B7280', fontSize: '1.125rem' }}>
            Detailed player performance metrics
          </p>
        </motion.div>


        {/* Player Stats Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem 0' }}>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            >
              <Trophy size={48} style={{ margin: '0 auto 1rem', color: '#10B981' }} />
            </motion.div>
            <p style={{ color: '#6B7280', fontWeight: '600' }}>Loading player stats...</p>
          </div>
        ) : sortedPlayers.length > 0 ? (
          <>
            {/* Results Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              style={{ 
                marginBottom: '1rem', 
                textAlign: 'center', 
                color: '#6B7280',
                fontSize: '0.875rem',
                fontWeight: '600'
              }}
            >
              Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, sortedPlayers.length)} of {sortedPlayers.length} players
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              style={{
                backgroundColor: 'white',
                borderRadius: '1rem',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
                overflow: 'hidden'
              }}
            >
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)', borderBottom: '2px solid #E5E7EB' }}>
                      <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '700', color: '#374151', fontSize: '0.875rem' }}>
                        Rank
                      </th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '700', color: '#374151', fontSize: '0.875rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }} onClick={() => toggleSort('name')}>
                          Player
                          {sortBy === 'name' && (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                        </div>
                      </th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '700', color: '#374151', fontSize: '0.875rem' }}>Team</th>
                      <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '700', color: '#374151', fontSize: '0.875rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', cursor: 'pointer' }} onClick={() => toggleSort('matchesPlayed')}>
                          <TrendingUp size={16} />
                          Matches
                          {sortBy === 'matchesPlayed' && (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                        </div>
                      </th>
                      <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '700', color: '#374151', fontSize: '0.875rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', cursor: 'pointer' }} onClick={() => toggleSort('aces')}>
                          <Target size={16} />
                          Aces
                          {sortBy === 'aces' && (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                        </div>
                      </th>
                      <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '700', color: '#374151', fontSize: '0.875rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', cursor: 'pointer' }} onClick={() => toggleSort('kickSmashes')}>
                          <Zap size={16} />
                          Smashes
                          {sortBy === 'kickSmashes' && (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                        </div>
                      </th>
                      <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '700', color: '#374151', fontSize: '0.875rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', cursor: 'pointer' }} onClick={() => toggleSort('unforcedErrors')}>
                          Errors
                          {sortBy === 'unforcedErrors' && (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                        </div>
                      </th>
                      <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '700', color: '#374151', fontSize: '0.875rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', cursor: 'pointer' }} onClick={() => toggleSort('akhlaaqRating')}>
                          <Award size={16} />
                          Rating
                          {sortBy === 'akhlaaqRating' && (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPlayers.map((player, index) => {
                      const team = player.teamId ? teams[player.teamId] : null;
                      const globalRank = indexOfFirstItem + index + 1;
                      const isTop3 = globalRank <= 3;
                      
                      return (
                        <tr
                          key={player.id}
                          style={{
                            backgroundColor: isTop3 ? 
                              (globalRank === 1 ? '#FEF3C7' : globalRank === 2 ? '#E5E7EB' : '#FEE2E2') : 
                              (index % 2 === 0 ? 'white' : '#F9FAFB'),
                            borderBottom: '1px solid #E5E7EB',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            if (!isTop3) e.currentTarget.style.backgroundColor = '#F3F4F6';
                          }}
                          onMouseLeave={(e) => {
                            if (!isTop3) e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#F9FAFB';
                          }}
                        >
                          {/* Rank */}
                          <td style={{ padding: '1rem', textAlign: 'center' }}>
                            {isTop3 ? (
                              <div style={{
                                width: '32px',
                                height: '32px',
                                margin: '0 auto',
                                borderRadius: '50%',
                                background: globalRank === 1 ? 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)' :
                                            globalRank === 2 ? 'linear-gradient(135deg, #D1D5DB 0%, #9CA3AF 100%)' :
                                            'linear-gradient(135deg, #FECACA 0%, #F87171 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 'bold',
                                color: 'white',
                                fontSize: '0.875rem',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                              }}>
                                #{globalRank}
                              </div>
                            ) : (
                              <span style={{ fontWeight: '600', color: '#6B7280' }}>#{globalRank}</span>
                            )}
                          </td>

                          {/* Player */}
                          <td style={{ padding: '1rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              {player.profileImage ? (
                                <img
                                  src={player.profileImage}
                                  alt={player.name}
                                  style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                                />
                              ) : (
                                <div style={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '50%',
                                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <User size={20} style={{ color: 'white' }} />
                                </div>
                              )}
                              <div>
                                <div style={{ fontWeight: '600', color: '#1F2937' }}>{player.name}</div>
                                <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>{player.position || 'Player'}</div>
                              </div>
                            </div>
                          </td>

                          {/* Team */}
                          <td style={{ padding: '1rem' }}>
                            {team ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                {team.logo && (
                                  <img 
                                    src={team.logo} 
                                    alt={team.name}
                                    style={{ width: '24px', height: '24px', borderRadius: '4px', objectFit: 'cover' }}
                                  />
                                )}
                                <span style={{ color: '#374151', fontWeight: '500', fontSize: '0.875rem' }}>{team.name}</span>
                              </div>
                            ) : (
                              <span style={{ color: '#9CA3AF', fontSize: '0.875rem' }}>No Team</span>
                            )}
                          </td>

                          {/* Matches Played */}
                          <td style={{ padding: '1rem', textAlign: 'center' }}>
                            <span style={{
                              background: '#DBEAFE',
                              color: '#1E40AF',
                              padding: '0.25rem 0.75rem',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem',
                              fontWeight: '700'
                            }}>
                              {player.matchesPlayed || 0}
                            </span>
                          </td>

                          {/* Aces */}
                          <td style={{ padding: '1rem', textAlign: 'center' }}>
                            <span style={{
                              background: '#D1FAE5',
                              color: '#065F46',
                              padding: '0.25rem 0.75rem',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem',
                              fontWeight: '700'
                            }}>
                              {player.aces || 0}
                            </span>
                          </td>

                          {/* Kick Smashes */}
                          <td style={{ padding: '1rem', textAlign: 'center' }}>
                            <span style={{
                              background: '#FEF3C7',
                              color: '#92400E',
                              padding: '0.25rem 0.75rem',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem',
                              fontWeight: '700'
                            }}>
                              {player.kickSmashes || 0}
                            </span>
                          </td>

                          {/* Unforced Errors */}
                          <td style={{ padding: '1rem', textAlign: 'center' }}>
                            <span style={{
                              background: '#FEE2E2',
                              color: '#DC2626',
                              padding: '0.25rem 0.75rem',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem',
                              fontWeight: '700'
                            }}>
                              {player.unforcedErrors || 0}
                            </span>
                          </td>

                          {/* Akhlaaq Rating */}
                          <td style={{ padding: '1rem', textAlign: 'center' }}>
                            <span style={{
                              background: '#F0FDF4',
                              color: '#059669',
                              padding: '0.25rem 0.75rem',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem',
                              fontWeight: '700'
                            }}>
                              {player.akhlaaqRating ? player.akhlaaqRating.toFixed(1) : '0.0'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginTop: '2rem',
                  flexWrap: 'wrap'
                }}
              >
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '0.5rem',
                    border: '1px solid #E5E7EB',
                    background: currentPage === 1 ? '#F3F4F6' : 'white',
                    color: currentPage === 1 ? '#9CA3AF' : '#1C563D',
                    fontWeight: '600',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Previous
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '0.5rem',
                      border: '1px solid #E5E7EB',
                      background: currentPage === page ? 'linear-gradient(135deg, #1C563D 0%, #2D7A5A 100%)' : 'white',
                      color: currentPage === page ? 'white' : '#1C563D',
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: currentPage === page ? '0 4px 12px rgba(28, 86, 61, 0.3)' : 'none'
                    }}
                  >
                    {page}
                  </button>
                ))}
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '0.5rem',
                    border: '1px solid #E5E7EB',
                    background: currentPage === totalPages ? '#F3F4F6' : 'white',
                    color: currentPage === totalPages ? '#9CA3AF' : '#1C563D',
                    fontWeight: '600',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Next
                </button>
              </motion.div>
            )}
          </motion.div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '3rem 0' }}>
            <Trophy size={64} style={{ margin: '0 auto 1rem', color: '#9CA3AF' }} />
            <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#374151', marginBottom: '0.5rem' }}>
              No Player Stats Yet
            </h3>
            <p style={{ color: '#6B7280' }}>
              Player statistics will appear here after matches are completed
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerStatsPage;

